/**
 * # Money
 *
 * This module contains utility functions and a facade over dinero.js
 * api, aiming to minimise abuse of these magic-but-dangerous objects
 * and bridging the translation gap between different contexts such as
 * user input, database storage and json/graphql transports
 *
 * We also include a few financial utilities such as pmt for calculating
 * fixed-interest-periodic repayment values.
 *
 * @module
 * @see https://v2.dinerojs.com/docs/api/mutations/add for full dinero.js api reference
 */

import { GBP } from '@dinero.js/currencies'
import {
  dinero,
  Dinero,
  toSnapshot,
  allocate,
  DineroSnapshot,
  Currency,
  toFormat,
  add,
  toUnit,
  transformScale,
} from 'dinero.js'
import { z } from 'zod'
import { isEqual } from 'lodash'

import { assert } from './assert'
import { DecimalJsLike } from './decimal'

export { GBP, USD, EUR, CAD, AUD } from '@dinero.js/currencies'
export * as currencies from '@dinero.js/currencies'

export type { DineroSnapshot as Snapshot, Currency }

export {
  toUnit,
  dinero as fromSnapshot,
  dinero as create,
  toSnapshot,
  allocate,
  multiply,
  add,
  subtract,
  convert,
  equal,
  compare,
  greaterThan,
  greaterThanOrEqual,
  lessThan,
  lessThanOrEqual,
  minimum,
  maximum,
  isZero,
  isPositive,
  isNegative,
  haveSameAmount,
  haveSameCurrency,
  hasSubUnits,
  toFormat,
  transformScale,
} from 'dinero.js'

/**
 * Monetary represenation, by default using `number` as stored value
 */
export type Money<Amount = number> = Dinero<Amount>

/**
 * Constant representation of 0 monetary value in GBP
 */
export const zeroPounds = dinero({ amount: 0, currency: GBP })

/**
 * Constant representation of 0 monetary value in specified currency
 */
export const zero = (currency: Currency<number>, scale?: number) =>
  dinero({ amount: 0, currency, scale })

/**
 * Calculate a fixed period payment to pay a balance (different in
 * present and future value) over a fixed period with a fixed rate
 *
 * @note make sure both dinero objects are of the same currency and scale before using pmt
 * @note monetary values cannot always be split perfectly (ie. £1 over 3 months would really require some order of payments such as 33p, 33p, 34p) so pmt is used where we accept some marginal loss of value
 * @note added new optional desiredScale input, which applies the new scale to the inputs
 * and performs the calculation to that precision. This was motivated by rounding error in pmt calculations.
 * @see http://stackoverflow.com/questions/2094967/excel-pmt-function-in-js
 * @see https://corporatefinanceinstitute.com/resources/excel/functions/pmt-function/
 * @todo Use dinero functions throughout rather than calling toUnit and
 *   then recreating dinero object
 */
export const pmt = (
  ratePerPeriod: number,
  periods: number,
  presentValue: Dinero<number>,
  futureValue: Dinero<number>,
  due: 'end' | 'beginning',
  desiredScale?: number
) => {
  const presentValueSnapshot = toSnapshot(presentValue)
  const futureValueSnapshot = toSnapshot(futureValue)
  const { currency, scale: inputScale } = presentValueSnapshot

  // some safety assertions to ensure developer has passed in matching scales
  // otherwise doing calculations off of a raw snapshot.amount would fail horribly
  assert(
    isEqual(presentValueSnapshot.currency, futureValueSnapshot.currency),
    'pmt can only be used on values of same currency'
  )

  let scale = inputScale
  let presentValueParsed = presentValue
  let futureValueParsed = futureValue

  if (desiredScale) {
    scale = desiredScale

    presentValueParsed = transformScale(presentValue, scale)
    futureValueParsed = transformScale(futureValue, scale)
  } else {
    assert(
      presentValueSnapshot.scale === futureValueSnapshot.scale,
      'pmt can only be used on values of same scale'
    )
  }

  const _futureValue = toSnapshot(futureValueParsed).amount
  const _presentValue = toSnapshot(presentValueParsed).amount

  // if an interest rate is actually set
  if (ratePerPeriod !== 0 && periods !== 0) {
    const type = due === 'end' ? 0 : 1
    const q = Math.pow(1 + ratePerPeriod, periods)
    const value =
      -(ratePerPeriod * (_futureValue + q * _presentValue)) /
      ((-1 + q) * (1 + ratePerPeriod * type))

    return dinero({ amount: Math.round(value), currency, scale })
  }

  // no interest, but do have periods to spread over
  if (periods !== 0) {
    const value = -(_futureValue + _presentValue) / periods

    return dinero({ amount: Math.round(value), currency, scale })
  }

  return dinero({ amount: 0, currency })
}

/**
 * Calculate a new monetary value as a percentage of an input monetary
 * value
 *
 * @param value The value to calculate a percentage of
 * @param share The share is a decimal value (typically between 0-1)
 *   reopresenting a percentage
 * @note monetary values cannot always be split perfectly so consider that this returned value will be rounded up or down
 * @note shares over 100% are not supported, look at multiplcation instead
 * @see https://v2.dinerojs.com/docs/faq/how-do-i-calculate-a-percentage
 */
export const percentage = (value: Dinero<number>, share: number) => {
  const rest = 1 - share
  const [chunk] = allocate(value, [share, rest])

  return chunk
}

/**
 * Create a dinero object from a float unit snapshot - an inverse of the
 * toUnit() utility
 *
 * @see toUnit
 * @see https://v2.dinerojs.com/docs/faq/how-can-i-create-dinero-objects-from-floats
 */
export const fromUnit = (
  float: number,
  currency: Currency<number>,
  scale?: number
) => {
  const factor = currency.base ** currency.exponent
  const amount = scale
    ? Math.round(float * Math.pow(10, scale))
    : Math.round(float * factor)

  return scale
    ? dinero({ amount, currency, scale })
    : dinero({ amount, currency })
}

/**
 * Prefer fromUnit() with same signature
 *
 * @deprecated
 * @see fromUnit
 */
export const fromFloat = fromUnit

/**
 * Create a dinero object from a decimal.js / Prisma.Decimal value
 *
 * @see fromFloat
 * @see https://www.prisma.io/docs/reference/api-reference/prisma-schema-reference#postgresql-5
 */
export const fromDecimal = (
  value: DecimalJsLike,
  currency: Currency<number>,
  scale?: number
) => {
  return scale
    ? fromUnit(value.toNumber(), currency, scale)
    : fromUnit(value.toNumber(), currency)
}

/**
 * Convert a dinero money object to the string representation of a
 * decimal.js / Prisma.Decimal value for Decimal serialisation
 */
export const toDecimalString = (value: Money) => {
  return toSnapshot(value).amount.toString()
}

/**
 * Format a monetary (dinero) value to a localised format
 *
 * @see https://v2.dinerojs.com/docs/guides/formatting-in-a-multilingual-site
 */
export const toIntlFormat = (
  value: Dinero<number>,
  locale: string,
  options: Omit<Intl.NumberFormatOptions, 'style' | 'currency'> = {}
) => {
  return toFormat(value, ({ amount, currency }) =>
    amount.toLocaleString(locale, {
      minimumFractionDigits: 0,
      ...options,
      style: 'currency',
      currency: currency.code,
    })
  )
}

/**
 * Sums the value of many (N) monetary values
 *
 * @see https://v2.dinerojs.com/docs#modular-and-composable
 */
export const addMany = (addends: Gaia.Money[]) => {
  return addends.reduce(add)
}

/**
 * Validate a MoneySnapshot
 */
const MoneySnapshot = z.object({
  amount: z.number(),
  currency: z.object({
    code: z.string(),
    base: z.number(),
    exponent: z.number(),
  }),
  scale: z.number(),
})

/**
 * Inferred type from schema
 */
export type MoneySnapshot = z.infer<typeof MoneySnapshot>

/**
 * Validate and parse either a Money Snapshot or Dinero Object into a
 * Money Snapshot
 */
export const MoneySnapshotSchema = z
  .union([MoneySnapshot, z.object({ calculator: z.any() }).passthrough()])
  .transform((value) => {
    if ('calculator' in value) {
      return toSnapshot(value as Dinero<number>)
    } else return value as MoneySnapshot
  })

export const sum = (opts: {
  arrayOfMoney: Dinero<number>[]
  initialValue?: Dinero<number>
}) => {
  let value = opts.initialValue ?? fromUnit(0, GBP)
  for (let item of opts.arrayOfMoney) {
    value = add(item, value)
  }
  return value
}

export const irr = ({
  cashFlowArray,
  guess = 0.1,
}: {
  cashFlowArray: Dinero<number>[]
  guess?: number
}) => {
  let MAX_ITER = 100
  let ACCURACY = 0.00001 / 100 // 0.000001

  let npv = function (rate: number) {
    let npv = 0
    for (let i = 0; i < cashFlowArray.length; i++) {
      npv += toUnit(cashFlowArray[i]) / Math.pow(1 + rate, i)
    }
    return npv
  }

  let irr = guess
  let iteration = 0
  let closeEnough = ACCURACY + 1
  let guessIncrement = 0

  while (closeEnough > ACCURACY && iteration < MAX_ITER) {
    let npvValue = npv(irr)
    let irrPrime = 0
    let irrDoublePrime = 0

    for (let i = 0; i < cashFlowArray.length; i++) {
      irrPrime -= (i * toUnit(cashFlowArray[i])) / Math.pow(1 + irr, i + 1)
      irrDoublePrime -=
        ((i + 1) * (i + 1) * toUnit(cashFlowArray[i])) /
        Math.pow(1 + irr, i + 2)
    }

    let nextIrr =
      irr -
      (npvValue / irrPrime) *
        (1 + (npvValue * irrDoublePrime) / (2 * irrPrime * irrPrime))
    closeEnough = Math.abs(nextIrr - irr)

    // handle NaN or diverging
    if (nextIrr === Infinity || Number.isNaN(nextIrr) || nextIrr < 0) {
      // if irr becomes negative, guess needs to go down
      guessIncrement += nextIrr < 0 ? -0.05 : 0.05
      irr = guess + guessIncrement
    } else {
      irr = nextIrr
    }

    iteration++
  }

  return irr
}

/**
 * Calculate a monthly compounded interest from a fixed annual interest
 * rate - typically used as a pmt input for monthly periods
 */
export const monthlyInterestRate = (annualInterestRate: number) => {
  return (1 + annualInterestRate) ** (1 / 12) - 1
}

/**
 * Simple helper to get currency from money object
 *
 * @param money
 * @returns
 */
export const getCurrency = (money: Money) => toSnapshot(money).currency

/**
 * Assert all elements of input money have the same currency, and return
 * that currency code. Throw if they do not have the same currency.
 *
 * @param money
 * @returns
 */
export const assertSameCurrency = (money: Money[]) => {
  const [first, ...rest] = money

  const targetCurrency = getCurrency(first)

  assert(
    rest.every((item) => isEqual(getCurrency(item), targetCurrency)),
    'Currencies are not the same'
  )

  return targetCurrency
}

export enum CurrencyString {
  Dollars = 'dollars',
  Pounds = 'pounds',
}
